import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

import {
  IResourceComponentsProps,
  useShow,
  useNotification,
  useCreate,
  usePermissions,
  useOne,
  useParsed,
  useDelete,
  useUpdate,
} from "@refinedev/core";
import {
  GlobalStyles,
  Grid,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  ButtonGroup,
  Button,
  Autocomplete,
  FormControl,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";

import { Show, SaveButton } from "@refinedev/mui";
import {
  SkipPrevious,
  SkipNext,
  Save,
  RemoveCircle,
  FirstPage,
  LastPage,
} from "@mui/icons-material";

import { MuiFileInput } from "mui-file-input";
import { useForm, FieldValues, Control, Controller } from "react-hook-form";

import { BackButton } from "components/BackButton";

import { commonValues } from "constants/common.values";
import { theme } from "constants/theme";

import {
  FormEntity,
  SectionCountEntityTypeEnum,
  UserEntityRoleEnum,
  FileEntity,
  SectionEntityTypeEnum,
  SectionEntity,
  FieldEntity,
  FieldEntityFileTypeEnum,
  FieldEntityTypeEnum,
} from "generated";
import { formatObject } from "utils/format";

export type SectionExtension = {
  secIndex?: number;
  isLast: boolean;
};

const InputComponent = ({
  field: {
    type,
    name,
    label,
    required,
    placeholder,
    acceptableValues,
    acceptableValuesKey,
    fields,
    fileType,
    globalRef,
  },
  namePrefix,
  control,
  requireFields,
  isNested = false,
  onFieldChange,
  onFileChange,
  showErrorMessage,
  formType,
  filesData,
  removeFileUrl,
}: {
  field: FieldEntity;
  namePrefix: string;
  control: Control<FieldValues, {}>;
  requireFields: boolean;
  isNested?: boolean;
  onFieldChange?: (value: number) => void;
  onFileChange?: (
    value: File[],
    namePrefix: string,
    nestedFieldName?: string,
    filesUrl?: string[]
  ) => void;
  showErrorMessage: (message: string) => void;
  formType?: "view" | "edit";
  filesData?: FilesDataType;
  removeFileUrl?: (info: {
    prefix: string;
    urls: string[];
    removeFrom: "file_data";
  }) => void;
}) => {
  const isRequired = isNested
    ? requireFields
    : requireFields
    ? required
    : false;

  const disabled = formType === "view";

  const { data: dropdownData, isLoading: isLoadingDropdown } = useOne({
    resource: "acceptable-values",
    id: acceptableValuesKey,
    queryOptions: {
      enabled: !!acceptableValuesKey && !acceptableValues?.length,
    },
  });

  return (
    <FormControl variant="standard">
      <Typography marginY={1}>{`${label}${isRequired ? " *" : ""}`}</Typography>

      {type === FieldEntityTypeEnum.String ||
      type === FieldEntityTypeEnum.Number ? (
        <Controller
          name={namePrefix + name}
          control={control}
          defaultValue={""}
          rules={{
            required: isRequired,
          }}
          render={({ field: { ref, ...field }, fieldState }) => {
            return (
              <TextField
                {...field}
                disabled={disabled}
                id={namePrefix + name}
                label={`${label}${isRequired ? " *" : ""}`}
                inputRef={ref}
                error={fieldState.invalid}
                helperText={fieldState.invalid ? "This field is required" : ""}
                onChange={(event) => {
                  let value: string | number;
                  if (type === FieldEntityTypeEnum.Number) {
                    value = globalRef
                      ? parseInt(event.target.value)
                      : parseFloat(event.target.value);

                    if (isNaN(value)) {
                      value = "";
                    } else if ((globalRef && value <= 0) || value < 0) {
                      value = 1;
                    }
                  } else {
                    value = event.target.value;
                  }

                  field.onChange(value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;

                  if (
                    type === FieldEntityTypeEnum.Number &&
                    globalRef &&
                    value
                  ) {
                    onFieldChange?.(parseInt(value));
                  }
                }}
                type={type}
                placeholder={placeholder || label}
              />
            );
          }}
        />
      ) : type === FieldEntityTypeEnum.SingleChoice ||
        type === FieldEntityTypeEnum.MultiChoice ? (
        <Controller
          name={namePrefix + name}
          control={control}
          defaultValue={type === FieldEntityTypeEnum.MultiChoice ? [] : null}
          rules={{
            required: isRequired,
          }}
          render={({ field: { ref, ...field }, fieldState }) => {
            const multiple = type === FieldEntityTypeEnum.MultiChoice;
            return (
              <Autocomplete
                {...field}
                disabled={disabled}
                multiple={multiple}
                loading={isLoadingDropdown}
                options={
                  (acceptableValues?.length
                    ? acceptableValues
                    : dropdownData?.data?.values) || []
                }
                disableCloseOnSelect={multiple}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={namePrefix + name}
                    label={`${label}${isRequired ? " *" : ""}`}
                    inputRef={ref}
                    placeholder={placeholder}
                    error={fieldState.invalid}
                    helperText={
                      fieldState.invalid ? "This field is required" : ""
                    }
                  />
                )}
                onChange={(_, value) => field.onChange(value)}
              />
            );
          }}
        />
      ) : type === FieldEntityTypeEnum.Fields ? (
        <Box
          display={"flex"}
          flexDirection={"column"}
          flex={1}
          gap={theme.gap}
          border={theme.borderPrimary}
          p={theme.commonPadding}
          borderRadius={theme.borderRadius}
        >
          {fields?.map((nestedField) => {
            const nestedNamePrefix = namePrefix + name + ".";
            return (
              <InputComponent
                key={nestedField.id}
                field={nestedField}
                namePrefix={nestedNamePrefix}
                control={control}
                requireFields={requireFields ? required : false}
                isNested={true}
                showErrorMessage={showErrorMessage}
                onFileChange={onFileChange}
                formType={formType}
                filesData={filesData}
                removeFileUrl={removeFileUrl}
              />
            );
          })}
        </Box>
      ) : type === FieldEntityTypeEnum.Files ? (
        <Controller
          name={namePrefix + name}
          control={control}
          rules={{
            required: isRequired,
          }}
          render={({ field: { ref, ...field }, fieldState }) => {
            let filesUrl: string[] = [];

            filesUrl = filesData?.[namePrefix + name]?.urls || [];

            return (
              <>
                {typeof field.value?.[0] === "string" || filesUrl.length ? (
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    flexWrap={"wrap"}
                    gap={theme.gap}
                    mb={2}
                  >
                    {(typeof field.value?.[0] === "string"
                      ? field.value
                      : filesUrl
                    ).map((value: string, i: number) => {
                      return (
                        <Box key={value + i} position={"relative"}>
                          {fileType === FieldEntityFileTypeEnum.Image ? (
                            <img
                              height={"200"}
                              width={"200"}
                              src={value}
                              alt={label + " - " + (i + 1)}
                            />
                          ) : (
                            <Button variant="outlined">
                              <NavLink
                                style={{ textDecoration: "none" }}
                                target="_blank"
                                to={value}
                              >
                                {fileType + " - " + label + " - " + (i + 1)}
                              </NavLink>
                            </Button>
                          )}

                          {formType === "edit" ? (
                            <IconButton
                              sx={{
                                position: "absolute",

                                top: -20,
                                right: -20,
                              }}
                              onClick={() => {
                                const showingFromForm =
                                  typeof field.value?.[0] === "string";

                                const filteredUrl = (
                                  (showingFromForm
                                    ? field.value
                                    : filesUrl) as string[]
                                ).filter((_, index) => index !== i);

                                if (showingFromForm) {
                                  field.onChange(
                                    filteredUrl.length ? filteredUrl : ""
                                  );
                                } else {
                                  removeFileUrl?.({
                                    prefix: namePrefix + name,
                                    removeFrom: "file_data",
                                    urls: filteredUrl,
                                  });
                                }
                              }}
                            >
                              <RemoveCircle color="error" />
                            </IconButton>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                ) : formType === "view" ? (
                  <>
                    <Typography
                      sx={{
                        ":first-letter": {
                          textTransform: "capitalize",
                        },
                      }}
                      p={theme.commonPadding}
                      border={theme.borderPrimary}
                      borderRadius={theme.borderRadius}
                      variant="caption"
                    >
                      {`${fileType} not added`}
                    </Typography>
                  </>
                ) : null}

                {disabled ? null : (
                  <MuiFileInput
                    {...field}
                    id={namePrefix + name}
                    label={label}
                    disabled={disabled}
                    inputRef={ref}
                    value={
                      typeof field.value?.[0] === "string"
                        ? undefined
                        : field.value
                    }
                    helperText={fieldState.invalid ? "File is required" : ""}
                    error={fieldState.invalid}
                    onChange={(files) => {
                      if (!fileType) return;

                      if (files.length > commonValues.maxFilesLength) {
                        showErrorMessage(
                          `Max number of allowed files is ${commonValues.maxFilesLength}`
                        );
                        return;
                      }
                      if (fileType !== FieldEntityFileTypeEnum.File) {
                        const hasInvalidType = Array.from(files).some(
                          (file) => !file.type.includes(fileType)
                        );

                        if (hasInvalidType) {
                          showErrorMessage("Inavlid file type found");
                          return;
                        }
                      }

                      if (files.length) {
                        const hasExceededSize = Array.from(files).some(
                          (file) =>
                            file.size / (1024 * 1024) > commonValues.maxFileSize
                        );

                        if (hasExceededSize) {
                          showErrorMessage(
                            `Max file size is ${commonValues.maxFileSize} MB`
                          );
                          return;
                        }
                      }

                      if (typeof field.value?.[0] === "string") {
                        filesUrl = field.value;
                      }
                      field.onChange(files);

                      onFileChange?.(
                        files,
                        namePrefix + name,
                        isNested ? name : undefined,
                        filesUrl
                      );
                    }}
                    multiple
                    placeholder={placeholder || label}
                  />
                )}
              </>
            );
          }}
        />
      ) : null}
    </FormControl>
  );
};

export type FilesDataType = Record<
  string,
  {
    sectionName: string;
    fieldName: string;
    nestedFieldName?: string;
    sectionIndex: number;
    urls?: string[];
  }
>;

export const FormShowComponent: React.FC<IResourceComponentsProps> = () => {
  const parsedData = useParsed();
  const responseId = parsedData.params?.responseId as string | undefined;
  const recordId = parsedData.params?.recordId as string | undefined;
  const formType = parsedData.params?.type as "view" | "edit" | undefined;

  const isViewing = formType === "view";
  const isEditing = formType === "edit";

  const [formData, setFormData] = useState<FormEntity | undefined>();

  const [hasAddedSections, setHasAddedSections] = useState(false);

  const [currentIndexInfo, setCurrentIndexInfo] = useState<{
    currentIndex: number;
    action?: "prev" | "next";
  }>({
    currentIndex: 0,
  });

  const [filesData, setFilesData] = useState<FilesDataType>({});

  const [optionalSectionsInfo, setOptionalSectionsInfo] =
    useState<Record<string, boolean>>();

  const [refInfo, setRefInfo] = useState<
    Record<
      string,
      {
        type: SectionCountEntityTypeEnum;
        sectionName: string;
        fieldValue: number;
      }
    >
  >({});

  const { queryResult } = useShow();
  const { data, isLoading, isSuccess } = queryResult;

  const { data: responseData, isSuccess: isResponseSuccess } = useOne({
    resource: `forms/${parsedData.id}/responses/${responseId}`,
    id: "",
    queryOptions: {
      enabled: !!responseId && !!formData?.id,
    },
  });

  const { open } = useNotification();

  const {
    control,
    handleSubmit,
    unregister,
    reset,
    setValue,
    formState: { dirtyFields },
  } = useForm();

  const { mutate, isLoading: isCreating, mutateAsync } = useCreate();

  const { mutate: updateResponseMutate, isLoading: isUpdatingResponse } =
    useUpdate();

  const { mutateAsync: deleteRecordMutate, isLoading: isDeletingRecord } =
    useDelete();

  const permissionsData = usePermissions();
  const isAdmin = permissionsData.data === UserEntityRoleEnum.Admin;

  const showUnsavedWarning =
    formType !== "view" && !!Object.keys(dirtyFields).length;

  const uploadFiles = async (
    data: FieldValues
  ): Promise<{ error: boolean; data: FieldValues }> => {
    let hasError = false;

    const clonedData: FieldValues = JSON.parse(JSON.stringify(data));

    const array = Object.values(filesData);
    for (let index = 0; index < array.length; index++) {
      const { sectionName, sectionIndex, fieldName, nestedFieldName, urls } =
        array[index];

      if (hasError) break;

      const section = data[sectionName].length
        ? data[sectionName][sectionIndex]
        : data[sectionName];

      const files = nestedFieldName
        ? section[fieldName][nestedFieldName]
        : section[fieldName];

      const clonedSection = clonedData[sectionName].length
        ? clonedData[sectionName][sectionIndex]
        : clonedData[sectionName];

      const clonedFiles = nestedFieldName
        ? clonedSection[fieldName][nestedFieldName]
        : clonedSection[fieldName];

      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      try {
        const responseData = (
          await mutateAsync({
            resource: "files",
            values: formData,
          })
        ).data as FileEntity[] | undefined;

        if (responseData) {
          for (let i = 0; i < responseData.length; i++) {
            const resData = responseData[i];

            if (resData.error) {
              showErrorMessage(
                `Error uploading file ${files[resData.index].name}`
              );
              hasError = true;
            } else {
              open?.({
                type: "success",
                message: `Upload ${index + 1} / ${
                  Object.keys(filesData).length
                } complete`,
              });
              clonedFiles[resData.index] = resData.url;
            }
          }

          if (urls?.length) {
            clonedFiles.splice(0, 0, ...urls);
          }
        } else {
          showErrorMessage("Error uploading files");
          hasError = true;
        }
      } catch (error) {
        showErrorMessage("Error uploading files");
        hasError = true;
      }
    }

    return { error: hasError, data: clonedData };
  };

  const onSubmit = async (data: FieldValues) => {
    if (currentIndexInfo.action === "next") {
      setCurrentIndexInfo((old) => ({
        currentIndex: old.currentIndex + 1,
      }));

      window?.scrollTo?.({ left: 0, top: 0, behavior: "smooth" });
      return;
    } else if (currentIndexInfo.action === "prev") {
      setCurrentIndexInfo((old) => ({
        currentIndex: old.currentIndex - 1,
      }));

      window?.scrollTo?.({ left: 0, top: 0, behavior: "smooth" });
      return;
    }

    const { error, data: dataWithUpload } = await uploadFiles(data);

    if (error) return;

    const formattedData = formatObject(dataWithUpload);

    Object.keys(formattedData).forEach((sec) => {
      const item = optionalSectionsInfo?.[sec];
      if (typeof item !== "undefined" && !item) {
        delete formattedData[sec];
      }
    });

    let hasError = false;
    Object.entries(refInfo).reduce((pv: any, cv: any) => {
      const value = refInfo[cv[0]].fieldValue;

      const sectionLength = formattedData[refInfo[cv[0]].sectionName]?.length;
      pv[cv[0]] = value;

      if (value !== sectionLength) {
        open?.({
          type: "error",
          message: `Reference count (${value}) doesnt match with number of sections (${sectionLength})`,
        });
        hasError = true;
      }
      return pv;
    }, {});

    formData?.sections.forEach((sec) => {
      if (
        sec.required &&
        sec.type === SectionEntityTypeEnum.Single &&
        !formattedData?.[sec.name]
      ) {
        formattedData[sec.name] = {};
      }
    });

    if (hasError) return;

    if (isEditing) {
      updateResponseMutate(
        {
          resource: `forms/${formData?.id}/responses`,
          id: responseId || "",
          meta: {
            method: "put",
          },
          values: { response: formattedData },
          successNotification: () => {
            return {
              description: "Response updated",
              message: `A form response successfully updated.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message,
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () =>
            window.location.replace(`/responses/${formData?.id}`),
        }
      );

      return;
    }
    mutate(
      {
        resource: `forms/${formData?.id}/responses`,
        values: { response: formattedData },
        successNotification: () => {
          return {
            description: "Response collected",
            message: `A form response successfully created.`,
            type: "success",
          };
        },
        errorNotification: (errors) => {
          return {
            description: errors?.message,
            message: errors?.errors?.length
              ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
              : "",
            type: "error",
          };
        },
      },
      {
        onSuccess: () => {
          if (recordId) {
            deleteRecordMutate(
              {
                resource: `attendances`,
                id: recordId,
                successNotification: () => {
                  return {
                    description: "Record removed",
                    message: `A form record successfully removed.`,
                    type: "success",
                  };
                },
                errorNotification: (errors) => {
                  return {
                    description: errors?.message,
                    message: errors?.errors?.length
                      ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                      : "",
                    type: "error",
                  };
                },
              },
              {
                onSuccess: () =>
                  window.location.replace(`/records/${formData?.id}`),
              }
            );
          } else {
            window.location.replace("/forms");
          }
        },
      }
    );
  };

  const addSectionHandler = ({
    lastSectionIndex,
    count = 1,
  }: {
    lastSectionIndex: number;
    count?: number;
  }) => {
    setFormData((old) => {
      if (!old) return old;

      let lastIndex = lastSectionIndex;

      let secIndex =
        (old.sections[lastSectionIndex] as SectionEntity & SectionExtension)
          ?.secIndex || 0;

      (
        old.sections[lastSectionIndex] as SectionEntity & SectionExtension
      ).isLast = false;

      const section: SectionEntity = JSON.parse(
        JSON.stringify(old.sections[lastSectionIndex])
      );

      for (let i = 0; i < count; i++) {
        const isLastItem = i === count - 1;

        (old.sections as (SectionEntity & SectionExtension)[]).splice(
          lastIndex + 1,
          0,
          {
            ...section,

            secIndex: secIndex + 1,

            isLast: isLastItem,
          }
        );

        secIndex++;
        lastIndex++;
      }

      if (lastSectionIndex < currentIndexInfo.currentIndex) {
        setCurrentIndexInfo((old) => ({
          currentIndex: old.currentIndex + count,
        }));
      }

      return { ...old };
    });
  };

  const removeSectionHandler = ({
    lastSectionIndex,
    count = 1,
  }: {
    lastSectionIndex: number;
    count?: number;
  }) => {
    setFormData((old) => {
      if (!old) return old;

      let lastIndex = lastSectionIndex;

      const name = old.sections[lastSectionIndex].name;
      let secIndex =
        (old.sections[lastSectionIndex] as SectionEntity & SectionExtension)
          ?.secIndex || 0;

      for (let i = 0; i < count; i++) {
        const isLastItem = i === count - 1;
        const namePrefix = `${name}.${secIndex}`;

        old.sections.splice(lastIndex, 1);

        (
          old.sections[lastIndex - 1] as SectionEntity & SectionExtension
        ).isLast = isLastItem;

        unregister(namePrefix);

        secIndex--;
        lastIndex--;
      }

      if (lastSectionIndex <= currentIndexInfo.currentIndex) {
        setCurrentIndexInfo((old) => ({
          currentIndex: old.currentIndex - count,
        }));
      }
      return { ...old };
    });
  };

  const onFieldChangeHandler = (value: number, globalRef: string) => {
    if (value === 0 || !formData || isNaN(value)) return;

    const sectionName = refInfo[globalRef].sectionName;

    const sectionValues = formData.sections.filter(
      (sec) => sec.name === sectionName
    );

    const sectionLength = sectionValues.length;

    refInfo[globalRef].fieldValue !== value &&
      setRefInfo((old) => ({
        ...old,
        [globalRef]: { ...old[globalRef], fieldValue: value },
      }));

    if (value === sectionLength) return;

    let lastIndex = formData.sections.findLastIndex(
      (sec) => sec.name === sectionName
    );

    if (value > sectionLength) {
      addSectionHandler({
        lastSectionIndex: lastIndex,
        count: value - sectionLength,
      });
    } else {
      removeSectionHandler({
        lastSectionIndex: lastIndex,
        count: sectionLength - value,
      });
    }
  };

  const showErrorMessage = (message: string) => {
    open?.({
      type: "error",
      message,
    });
  };

  useEffect(() => {
    if (data?.data?.id && isSuccess) {
      setFormData(JSON.parse(JSON.stringify(data.data)));
      data.data.sections.forEach((section: SectionEntity) => {
        !section.required &&
          setOptionalSectionsInfo((old) => ({
            ...old,
            [section.name]: section.required,
          }));

        if (section.count?.$ref) {
          const ref = section.count.$ref;
          const type = section.count.type as SectionCountEntityTypeEnum;
          setRefInfo((old) => ({
            ...old,
            [ref]: { sectionName: section.name, type, fieldValue: 1 },
          }));
        }
      });
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (
      isResponseSuccess &&
      responseData.data?.id &&
      formData?.id &&
      !hasAddedSections
    ) {
      reset(responseData.data.response);

      Object.keys(responseData.data.response).forEach((secKey) => {
        const isRequiredSection = formData.sections.find(
          (sec) => sec.name === secKey
        )?.required;

        !isRequiredSection &&
          setOptionalSectionsInfo((old) => ({
            ...old,
            [secKey]: true,
          }));

        if (Array.isArray(responseData.data.response[secKey])) {
          const length = responseData.data.response[secKey].length;
          if (length > 1) {
            const lastIndex = formData.sections.findLastIndex(
              (sec) => sec.name === secKey
            );

            const ref = formData.sections[lastIndex].count?.$ref;
            const type = formData.sections[lastIndex].count?.type;

            if (ref && type === SectionCountEntityTypeEnum.Number) {
              setRefInfo((old) => ({
                ...old,
                [ref]: { sectionName: secKey, type, fieldValue: length },
              }));
            }

            addSectionHandler({
              lastSectionIndex: lastIndex,
              count: length - 1,
            });
          }
        }
      });

      setHasAddedSections(true);
    }
  }, [isResponseSuccess, responseData, reset, formData, hasAddedSections]);

  if (!formData?.sections?.[currentIndexInfo.currentIndex]?.id) {
    return (
      <Show
        canEdit={isAdmin}
        title={formData?.label || "Form"}
        isLoading={isLoading}
      />
    );
  }

  const {
    id,
    label,
    type,
    fields,
    name,
    secIndex = 0,
    isLast = true,
    required,
  } = formData?.sections?.[currentIndexInfo.currentIndex] as SectionEntity &
    SectionExtension;

  const isMultiple = type === SectionEntityTypeEnum.Multiple;
  let namePrefix = isMultiple ? `${name}.${secIndex}.` : `${name}.`;

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            overscrollBehaviorY: "contain",
          },
        }}
      />

      <Show
        title={formData.label || "Form"}
        isLoading={isLoading}
        headerButtons={<></>}
        goBack={<BackButton showUnsavedWarning={showUnsavedWarning} />}
      >
        {isViewing ? (
          <Grid
            marginX={"auto"}
            container
            item
            md={10}
            justifyContent={"space-between"}
            mb={1}
          >
            {currentIndexInfo.currentIndex > 0 ? (
              <IconButton
                color="primary"
                onClick={() => {
                  setCurrentIndexInfo(() => ({
                    currentIndex: 0,
                    action: "prev",
                  }));
                }}
              >
                <FirstPage />
              </IconButton>
            ) : null}

            {currentIndexInfo.currentIndex < formData.sections.length - 1 ? (
              <IconButton
                color="primary"
                sx={{
                  marginLeft: "auto",
                }}
                onClick={() => {
                  setCurrentIndexInfo(() => ({
                    currentIndex: formData.sections.length - 1,
                    action: "next",
                  }));
                }}
              >
                <LastPage />
              </IconButton>
            ) : null}
          </Grid>
        ) : null}

        <Grid
          marginX={"auto"}
          border={theme.borderPrimary}
          borderRadius={theme.borderRadius}
          p={theme.commonPadding}
          container
          item
          md={10}
        >
          <Box
            component={"form"}
            onSubmit={handleSubmit(onSubmit)}
            display={"flex"}
            flexDirection={"column"}
            flex={1}
            gap={theme.gap}
          >
            <React.Fragment key={id + secIndex}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                flex={1}
                gap={theme.gap}
                border={theme.borderPrimary}
                p={theme.commonPadding}
                borderRadius={theme.borderRadius}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  alignItems={"flex-start"}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                >
                  <Typography variant={"h4"}>{`${label}${
                    isMultiple ? ` (${secIndex + 1})` : ""
                  }`}</Typography>

                  {!required && secIndex === 0 ? (
                    <FormControlLabel
                      label={"Require fields"}
                      id={id + secIndex}
                      control={
                        <Checkbox
                          name={id + secIndex}
                          checked={!!optionalSectionsInfo?.[name]}
                          onChange={(e) => {
                            setOptionalSectionsInfo((old) => ({
                              ...old,
                              [name]: e.target.checked,
                            }));
                          }}
                        />
                      }
                    />
                  ) : null}
                </Box>
                <Divider />

                {fields.map((field) => {
                  return (
                    <InputComponent
                      key={field.id}
                      field={field}
                      namePrefix={namePrefix}
                      control={control}
                      formType={formType}
                      filesData={filesData}
                      showErrorMessage={showErrorMessage}
                      onFieldChange={
                        field.globalRef &&
                        field.type === refInfo?.[field.globalRef]?.type
                          ? (value) => {
                              onFieldChangeHandler(
                                value,
                                field.globalRef as string
                              );
                            }
                          : undefined
                      }
                      onFileChange={(
                        files,
                        prefix,
                        nestedFieldName,
                        filesUrl
                      ) => {
                        if (files.length) {
                          !filesData?.[prefix] &&
                            setFilesData((old) => ({
                              ...old,
                              [prefix]: {
                                sectionName: name,
                                sectionIndex: secIndex,
                                fieldName: field.name,
                                nestedFieldName,
                                urls: filesUrl?.length ? filesUrl : undefined,
                              },
                            }));
                        } else {
                          filesData?.[prefix] &&
                            setFilesData((old) => {
                              old[prefix]?.urls?.length &&
                                setValue(prefix, old[prefix].urls);
                              delete old[prefix];
                              return old;
                            });
                        }
                      }}
                      removeFileUrl={({
                        prefix,
                        urls,
                        removeFrom,
                      }: {
                        prefix: string;
                        urls: string[];
                        removeFrom: "file_data";
                      }) => {
                        setFilesData((old) => {
                          old[prefix].urls = urls.length ? urls : undefined;
                          return { ...old };
                        });
                      }}
                      requireFields={
                        typeof optionalSectionsInfo?.[name] === "undefined"
                          ? required
                          : optionalSectionsInfo[name]
                      }
                    />
                  );
                })}
              </Box>
              {type === SectionEntityTypeEnum.Multiple &&
              isLast &&
              !isViewing ? (
                <ButtonGroup sx={{ alignSelf: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      addSectionHandler({
                        lastSectionIndex: currentIndexInfo.currentIndex,
                      })
                    }
                  >
                    Add row
                  </Button>

                  {secIndex > 0 ? (
                    <Button
                      variant="contained"
                      onClick={() =>
                        removeSectionHandler({
                          lastSectionIndex: currentIndexInfo.currentIndex,
                        })
                      }
                    >
                      Delete row
                    </Button>
                  ) : null}
                </ButtonGroup>
              ) : null}
            </React.Fragment>

            <ButtonGroup sx={{ justifyContent: "center", flexWrap: "wrap" }}>
              {currentIndexInfo.currentIndex > 0 ? (
                <SaveButton
                  type="submit"
                  startIcon={<SkipPrevious />}
                  onClick={() =>
                    setCurrentIndexInfo((old) => ({ ...old, action: "prev" }))
                  }
                  sx={{
                    flex: 1,
                  }}
                >
                  Prev
                </SaveButton>
              ) : null}
              {currentIndexInfo.currentIndex === formData.sections.length - 1 &&
              !isViewing ? (
                <SaveButton
                  disabled={
                    formData.status !== "PUBLISHED" ||
                    isCreating ||
                    isDeletingRecord ||
                    isUpdatingResponse
                  }
                  type="submit"
                  startIcon={<Save />}
                  endIcon={
                    isCreating || isDeletingRecord || isUpdatingResponse ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : undefined
                  }
                  onClick={() =>
                    setCurrentIndexInfo((old) => ({
                      currentIndex: old.currentIndex,
                    }))
                  }
                  sx={{
                    flex: 1,
                  }}
                >
                  SAVE
                </SaveButton>
              ) : null}
              {currentIndexInfo.currentIndex < formData.sections.length - 1 ? (
                <SaveButton
                  type="submit"
                  startIcon={undefined}
                  endIcon={<SkipNext />}
                  onClick={() =>
                    setCurrentIndexInfo((old) => ({ ...old, action: "next" }))
                  }
                  sx={{
                    flex: 1,
                  }}
                >
                  Next
                </SaveButton>
              ) : null}
            </ButtonGroup>
          </Box>
        </Grid>
      </Show>
    </>
  );
};
